/**
 * ShiftCreateModal - Modal for creating a new shift from calendar
 * Pre-fills date and time from calendar click/drag, requires user to select project and venue
 * PROMPT 32: Added duration auto-calculation
 */
import { useState, useEffect } from "react";
import { useCalendarEdit } from "./CalendarEditContext";
import { getProjects, getVenues, getVenueLocations, extractErrorMessage } from "../../lib/api";
import { useFormatters } from "../shared/useFormatters";
import { useShiftDuration } from "../../hooks/useShiftDuration";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { Loader2, Calendar, Clock, MapPin, FolderKanban, Users, AlertCircle, Timer, Sparkles, Info } from "lucide-react";
import { cn } from "../../lib/utils";
import { Switch } from "../ui/switch";

export function ShiftCreateModal({ defaultProjectId = null, defaultVenueId = null, defaultVenueLocationId = null }) {
  const { showCreateModal, newShiftData, closeCreateModal, saveNewShift, isSaving } = useCalendarEdit();
  const { formatDate } = useFormatters();
  
  // Duration auto-calculation hook
  const {
    startTime,
    endTime,
    duration,
    setStartTime,
    setEndTime,
    setDuration,
    reset: resetDuration,
    isEndTimeCalculated,
    isDurationCalculated,
  } = useShiftDuration({ useDefaults: false });
  
  // Form state (excluding time fields which are managed by the hook)
  const [formData, setFormData] = useState({
    name: "",
    project_id: "",
    venue_id: "",
    venue_location_id: "",
    date: "",
    staff_required: 1,
    out_of_town: false,
    notes: "",
    // EF-SHIFT-MEETING-PLACE-V1: Meeting place instructions
    meeting_place: "",
  });
  const [errors, setErrors] = useState({});
  
  // Dropdown data
  const [projects, setProjects] = useState([]);
  const [venues, setVenues] = useState([]);
  const [venueLocations, setVenueLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Load projects and venues
  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectsRes, venuesRes, venueLocationsRes] = await Promise.all([
          getProjects({ limit: 100, status: "active" }),
          getVenues({ limit: 100 }),
          getVenueLocations({ limit: 500 }),
        ]);
        setProjects(projectsRes.data || []);
        setVenues(venuesRes.data || []);
        setVenueLocations(venueLocationsRes.data || []);
      } catch (err) {
        console.error("Failed to load data:", extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    
    if (showCreateModal) {
      loadData();
    }
  }, [showCreateModal]);
  
  // Reset form when modal opens with new shift data
  useEffect(() => {
    if (showCreateModal && newShiftData) {
      setFormData({
        name: "",
        project_id: defaultProjectId || "",
        venue_id: defaultVenueId || "",
        venue_location_id: defaultVenueLocationId || "",
        date: newShiftData.date || "",
        staff_required: 1,
        out_of_town: false,
        notes: "",
        // EF-SHIFT-MEETING-PLACE-V1: Reset meeting place
        meeting_place: "",
      });
      setErrors({});
      // Reset duration hook with initial times from calendar drag
      resetDuration(
        newShiftData.start_time || "09:00",
        newShiftData.end_time || "17:00"
      );
    }
  }, [showCreateModal, newShiftData, defaultProjectId, defaultVenueId, defaultVenueLocationId, resetDuration]);
  
  // Handle input changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };
  
  // Handle venue change - reset venue_location_id
  const handleVenueChange = (value) => {
    setFormData((prev) => ({ ...prev, venue_id: value, venue_location_id: "" }));
    if (errors.venue_id) {
      setErrors((prev) => ({ ...prev, venue_id: null }));
    }
  };
  
  // Get filtered venue locations for selected venue
  const filteredVenueLocations = formData.venue_id 
    ? venueLocations.filter(vl => vl.venue_id === formData.venue_id)
    : [];
  
  // Handle time field changes with error clearing
  const handleStartTimeChange = (value) => {
    setStartTime(value);
    if (errors.start_time) {
      setErrors((prev) => ({ ...prev, start_time: null }));
    }
  };
  
  const handleEndTimeChange = (value) => {
    setEndTime(value);
    if (errors.end_time) {
      setErrors((prev) => ({ ...prev, end_time: null }));
    }
  };
  
  const handleDurationChange = (value) => {
    setDuration(value);
    if (errors.duration) {
      setErrors((prev) => ({ ...prev, duration: null }));
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Shift name is required";
    }
    if (!formData.project_id) {
      newErrors.project_id = "Project is required";
    }
    if (!formData.venue_id) {
      newErrors.venue_id = "Venue is required";
    }
    if (!formData.venue_location_id) {
      newErrors.venue_location_id = "Meeting point is required";
    }
    if (!formData.date) {
      newErrors.date = "Date is required";
    }
    if (!startTime) {
      newErrors.start_time = "Start time is required";
    }
    if (!endTime) {
      newErrors.end_time = "End time is required";
    }
    if (!duration || duration === "00:00") {
      newErrors.duration = "Duration must be greater than 0";
    }
    if (formData.staff_required < 1) {
      newErrors.staff_required = "At least 1 crew member required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Combine form data with time values from the duration hook
    const shiftData = {
      ...formData,
      start_time: startTime,
      end_time: endTime,
    };
    
    const result = await saveNewShift(shiftData);
    
    if (!result.success) {
      // Show API error
      setErrors((prev) => ({ ...prev, api: result.error }));
    }
  };
  
  // Handle cancel
  const handleCancel = () => {
    closeCreateModal();
  };
  
  // Get project default venue if available
  const selectedProject = projects.find((p) => p.id === formData.project_id);
  
  // Auto-select venue if project has a default venue
  useEffect(() => {
    if (selectedProject?.default_venue_id && !formData.venue_id) {
      setFormData((prev) => ({ ...prev, venue_id: selectedProject.default_venue_id }));
    }
  }, [selectedProject, formData.venue_id]);
  
  return (
    <Dialog open={showCreateModal} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="sm:max-w-[500px]" data-testid="shift-create-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Create New Shift
          </DialogTitle>
          <DialogDescription>
            Fill in the details below to create a new shift.
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* API Error */}
            {errors.api && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
                <span className="text-sm text-destructive">{errors.api}</span>
              </div>
            )}
            
            {/* Shift Name */}
            <div className="space-y-2">
              <Label htmlFor="shift-name">Shift Name *</Label>
              <Input
                id="shift-name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="e.g., Morning Shift, Evening Security"
                className={cn(errors.name && "border-destructive")}
                data-testid="shift-name-input"
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
            </div>
            
            {/* Project Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <FolderKanban className="h-3.5 w-3.5" />
                Project *
              </Label>
              <Select
                value={formData.project_id}
                onValueChange={(v) => handleChange("project_id", v)}
              >
                <SelectTrigger 
                  className={cn(errors.project_id && "border-destructive")}
                  data-testid="shift-project-select"
                >
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.project_id && <p className="text-xs text-destructive">{errors.project_id}</p>}
            </div>
            
            {/* Venue Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                Venue *
              </Label>
              <Select
                value={formData.venue_id}
                onValueChange={handleVenueChange}
              >
                <SelectTrigger 
                  className={cn(errors.venue_id && "border-destructive")}
                  data-testid="shift-venue-select"
                >
                  <SelectValue placeholder="Select venue" />
                </SelectTrigger>
                <SelectContent>
                  {venues.map((venue) => (
                    <SelectItem key={venue.id} value={venue.id}>
                      {venue.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.venue_id && <p className="text-xs text-destructive">{errors.venue_id}</p>}
            </div>
            
            {/* Location Selection - cascading from Venue */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                Location *
              </Label>
              <Select
                value={formData.venue_location_id}
                onValueChange={(v) => handleChange("venue_location_id", v)}
                disabled={!formData.venue_id}
              >
                <SelectTrigger 
                  className={cn(
                    errors.venue_location_id && "border-destructive",
                    !formData.venue_id && "opacity-50"
                  )}
                  data-testid="shift-venue-location-select"
                >
                  <SelectValue placeholder={formData.venue_id ? "Select location" : "Select venue first"} />
                </SelectTrigger>
                <SelectContent>
                  {filteredVenueLocations.map((vl) => (
                    <SelectItem key={vl.id} value={vl.id}>
                      {vl.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.venue_location_id && <p className="text-xs text-destructive">{errors.venue_location_id}</p>}
              {formData.venue_id && filteredVenueLocations.length === 0 && (
                <p className="text-xs text-muted-foreground">No locations defined for this venue</p>
              )}
            </div>
            
            {/* Date and Time Row */}
            <div className="grid grid-cols-4 gap-3">
              {/* Date */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Date *
                </Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange("date", e.target.value)}
                  className={cn(errors.date && "border-destructive")}
                  data-testid="shift-date-input"
                />
                {errors.date && <p className="text-xs text-destructive">{errors.date}</p>}
              </div>
              
              {/* Start Time */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  Start *
                </Label>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                  className={cn(errors.start_time && "border-destructive")}
                  data-testid="shift-start-time-input"
                />
                {errors.start_time && <p className="text-xs text-destructive">{errors.start_time}</p>}
              </div>
              
              {/* Duration */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Timer className="h-3.5 w-3.5" />
                  Duration *
                  {isDurationCalculated && (
                    <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 bg-primary/10 text-primary border-primary/30">
                      <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                      auto
                    </Badge>
                  )}
                </Label>
                <Input
                  type="time"
                  value={duration}
                  onChange={(e) => handleDurationChange(e.target.value)}
                  className={cn(
                    errors.duration && "border-destructive",
                    isDurationCalculated && "bg-primary/5 border-primary/30"
                  )}
                  data-testid="shift-duration-input"
                  title="Duration in HH:MM format"
                />
                {errors.duration && <p className="text-xs text-destructive">{errors.duration}</p>}
              </div>
              
              {/* End Time */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  End *
                  {isEndTimeCalculated && (
                    <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 h-4 bg-primary/10 text-primary border-primary/30">
                      <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                      auto
                    </Badge>
                  )}
                </Label>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => handleEndTimeChange(e.target.value)}
                  className={cn(
                    errors.end_time && "border-destructive",
                    isEndTimeCalculated && "bg-primary/5 border-primary/30"
                  )}
                  data-testid="shift-end-time-input"
                />
                {errors.end_time && <p className="text-xs text-destructive">{errors.end_time}</p>}
              </div>
            </div>
            
            {/* Crew Required */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                Crew Required *
              </Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={formData.staff_required}
                onChange={(e) => handleChange("staff_required", parseInt(e.target.value) || 1)}
                className={cn("w-24", errors.staff_required && "border-destructive")}
                data-testid="shift-staff-required-input"
              />
              {errors.staff_required && <p className="text-xs text-destructive">{errors.staff_required}</p>}
            </div>
            
            {/* Out of Town Toggle */}
            <div className="flex items-center justify-between p-3 bg-surface-base rounded-lg border border-border/50">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <div>
                  <Label htmlFor="out_of_town_create" className="text-sm font-medium text-foreground cursor-pointer">
                    Out of Town (OOT)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Enables automatic travel cost estimation (time, distance, toll)
                  </p>
                </div>
              </div>
              <Switch
                id="out_of_town_create"
                checked={formData.out_of_town}
                onCheckedChange={(checked) => handleChange("out_of_town", checked)}
                data-testid="shift-out-of-town-toggle"
              />
            </div>
            
            {/* OOT Info Banner - shown when OOT enabled */}
            {formData.out_of_town && (
              <div className="flex items-start gap-2 p-3 bg-surface-elevated rounded-lg border border-primary/30">
                <Info className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  Travel tasks will be added when you edit this shift. You can adjust quantities and rates there.
                </p>
              </div>
            )}
            
            {/* EF-SHIFT-MEETING-PLACE-V1: Meeting Place */}
            <div className="space-y-2">
              <Label htmlFor="shift-meeting-place">Meeting Place (optional)</Label>
              <Textarea
                id="shift-meeting-place"
                value={formData.meeting_place}
                onChange={(e) => handleChange("meeting_place", e.target.value)}
                placeholder="Enter meeting point instructions (e.g. Main entrance, backstage gate, dock access point)"
                rows={2}
                maxLength={1000}
                data-testid="shift-meeting-place-input"
              />
              <p className="text-xs text-muted-foreground">
                Where crew should meet on arrival
              </p>
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="shift-notes">Notes (optional)</Label>
              <Textarea
                id="shift-notes"
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder="Any additional notes for this shift..."
                rows={2}
                data-testid="shift-notes-input"
              />
            </div>
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSaving}
                data-testid="shift-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSaving}
                className="bg-primary hover:bg-primary/90"
                data-testid="shift-create-btn"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Shift"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ShiftCreateModal;
